import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { useCanvasReactions } from '@/hooks/useCanvasReactions';
import { useAuth } from '@/contexts/AuthContext';

interface CanvasItemHoverOverlayProps {
  itemId: string;
  itemOwnerId: string;
  itemOwnerName?: string;
  isHovered: boolean;
  onDoubleClick: () => void;
  rotation?: number;
}

export const CanvasItemHoverOverlay: React.FC<CanvasItemHoverOverlayProps> = ({
  itemId,
  itemOwnerId,
  itemOwnerName,
  isHovered,
  onDoubleClick,
  rotation = 0
}) => {
  const { user } = useAuth();
  const { likeCount, hasLiked, stickers } = useCanvasReactions(itemId);
  const isOwnItem = user?.id === itemOwnerId;

  if (isOwnItem || !isHovered) return null;

  return (
    <div 
      className="absolute inset-0 pointer-events-none"
      style={{
        transform: `rotate(${-rotation}deg)`, // Counter-rotate to keep overlay straight
        transformOrigin: 'center center'
      }}
    >
      {/* Author label - top left */}
      <div className="absolute -top-8 left-0 bg-black/80 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
        {itemOwnerName || 'Anonymous'}
      </div>

      {/* Stickers from other users */}
      {stickers.length > 0 && (
        <div className="absolute top-0 right-0 flex flex-wrap gap-1 max-w-[100px]">
          {stickers.slice(0, 5).map((sticker, idx) => (
            <div
              key={sticker.id}
              className="text-2xl animate-in fade-in zoom-in duration-200"
              style={{
                animationDelay: `${idx * 50}ms`
              }}
            >
              {sticker.sticker_emoji}
            </div>
          ))}
          {stickers.length > 5 && (
            <div className="text-xs text-white bg-black/60 rounded-full w-6 h-6 flex items-center justify-center">
              +{stickers.length - 5}
            </div>
          )}
        </div>
      )}

      {/* Like count - bottom right (outside object, not affected by rotation) */}
      {likeCount > 0 && (
        <div className="absolute -bottom-6 -right-6 bg-pink-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 shadow-lg">
          <Heart className="w-3 h-3" fill={hasLiked ? 'currentColor' : 'none'} />
          <span>{likeCount}</span>
        </div>
      )}

      {/* Double-click hint */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full mt-1 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded whitespace-nowrap opacity-60">
        Double-click to ❤️
      </div>
    </div>
  );
};
